import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  CommunicationStatus,
  ObjectTypeEnum,
  type ViewCommunication,
  type ViewCommunicationItem,
} from "@/briefpoint-client";
import {
  useCaseApi,
  useClientApi,
  useCommunicationApi,
  useDocumentApi,
} from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import {
  caseGetOptions,
  clientGetOptions,
  getCommunicationsForObjectOptions,
  getDocumentOptions,
} from "@/lib/query-options";
import { getSubTypeText } from "@/lib/utils";

/**
 * Read path: document, case, client, communications, and memos that depend only
 * on those queries (not on UI `language` state).
 */
export function useReviewClientTasksPageData() {
  const params = useParams<{ documentId: string }>();
  const documentId = params.documentId;
  const auth = useAuth();
  const user = auth.user;
  const documentApi = useDocumentApi();
  const caseApi = useCaseApi();
  const clientApi = useClientApi();
  const communicationApi = useCommunicationApi();

  const documentQuery = useQuery({
    ...getDocumentOptions(documentId, documentApi),
    enabled: !!documentId,
  });

  const caseQuery = useQuery({
    ...caseGetOptions(
      caseApi,
      user?.firmId,
      documentQuery.data?.caseId ?? undefined,
    ),
    enabled: !!user?.firmId && !!documentQuery.data?.caseId,
  });

  const clientQuery = useQuery({
    ...clientGetOptions(
      clientApi,
      user?.firmId,
      documentQuery.data?.clientId ?? "",
    ),
    enabled: !!user?.firmId && !!documentQuery.data?.clientId,
  });

  const communicationsQuery = useQuery({
    ...getCommunicationsForObjectOptions(
      communicationApi,
      documentId,
      ObjectTypeEnum.Document,
    ),
    enabled: !!documentId,
  });

  const reviewCommunications = communicationsQuery.data;

  const activeCommunication = useMemo((): ViewCommunication | undefined => {
    return reviewCommunications?.find(
      (c) =>
        c.status !== CommunicationStatus.Closed &&
        c.status !== CommunicationStatus.Deleted,
    );
  }, [reviewCommunications]);

  const isPageInitialDataLoading = useMemo(() => {
    if (!documentId) {
      return false;
    }
    if (documentQuery.isLoading) {
      return true;
    }
    if (communicationsQuery.isLoading) {
      return true;
    }
    if (!communicationsQuery.isSuccess && !communicationsQuery.isError) {
      return true;
    }
    if (user?.firmId && documentQuery.data?.caseId && caseQuery.isLoading) {
      return true;
    }
    if (user?.firmId && documentQuery.data?.clientId && clientQuery.isLoading) {
      return true;
    }
    return false;
  }, [
    documentId,
    user?.firmId,
    documentQuery.isLoading,
    documentQuery.data?.caseId,
    documentQuery.data?.clientId,
    communicationsQuery.isLoading,
    communicationsQuery.isSuccess,
    communicationsQuery.isError,
    caseQuery.isLoading,
    clientQuery.isLoading,
  ]);

  const isReviewLocked = useMemo(() => {
    const status = activeCommunication?.status;
    return (
      status === CommunicationStatus.Sent ||
      status === CommunicationStatus.Open ||
      status === CommunicationStatus.InProgress ||
      status === CommunicationStatus.ChangesRequested ||
      status === CommunicationStatus.Complete ||
      status === CommunicationStatus.Closed
    );
  }, [activeCommunication?.status]);

  const isNextStepBlocked =
    isReviewLocked &&
    activeCommunication?.status !== CommunicationStatus.Pending;

  const primaryTaskCount = useMemo(() => {
    const items = activeCommunication?.externalCommunicationItems?.filter(
      (c) => !c.translatedExternalCommunicationItemId,
    );
    return items?.length ?? 0;
  }, [activeCommunication?.externalCommunicationItems]);

  const hasNoTasks =
    documentQuery.isSuccess &&
    communicationsQuery.isSuccess &&
    primaryTaskCount === 0;

  const hasClientTasks =
    documentQuery.isSuccess &&
    communicationsQuery.isSuccess &&
    primaryTaskCount > 0;

  const isEmptyClientTasksState = hasNoTasks && !isReviewLocked;

  const interrogatoryIdsCoveredByPrimaryTasks = useMemo(() => {
    const ids = new Set<number>();
    const items =
      activeCommunication?.externalCommunicationItems?.filter(
        (c) => !c.translatedExternalCommunicationItemId,
      ) ?? [];
    for (const item of items) {
      for (const id of item.responseInfo?.associatedObjectIds ?? []) {
        const n = Number(id);
        if (Number.isFinite(n)) {
          ids.add(n);
        }
      }
    }
    return ids;
  }, [activeCommunication?.externalCommunicationItems]);

  const hasInterrogatoriesWithoutAnyClientTask = useMemo(() => {
    const interrogatories = documentQuery.data?.interrogatories ?? [];
    if (interrogatories.length === 0) {
      return false;
    }
    return interrogatories.some(
      (inv) => !interrogatoryIdsCoveredByPrimaryTasks.has(inv.id),
    );
  }, [
    documentQuery.data?.interrogatories,
    interrogatoryIdsCoveredByPrimaryTasks,
  ]);

  const isSimplifyAllRemainingButtonVisible =
    hasClientTasks && !isReviewLocked && hasInterrogatoriesWithoutAnyClientTask;

  const requestName = useMemo(() => {
    const doc = documentQuery.data;
    if (!doc?.info) return "Request";
    return getSubTypeText(doc.info.documentSubType, doc.info.jurisdiction ?? "")
      .request;
  }, [documentQuery.data]);

  const primaryTasksSorted = useMemo(() => {
    const interrogatories = documentQuery.data?.interrogatories ?? [];
    const interrogatoryIdToNumber = new Map<number, number>();
    for (let i = 0; i < interrogatories.length; i++) {
      const inv = interrogatories[i];
      interrogatoryIdToNumber.set(inv.id, inv.number);
    }

    const sortKeyForPrimaryTask = (
      item: ViewCommunicationItem,
    ): { numberKey: number; label: string } => {
      const ids = item.responseInfo?.associatedObjectIds ?? [];
      let minNumber = Number.POSITIVE_INFINITY;
      for (let j = 0; j < ids.length; j++) {
        const idNum = Number(ids[j]);
        if (!Number.isFinite(idNum)) {
          continue;
        }
        const n = interrogatoryIdToNumber.get(idNum);
        if (n !== undefined && n < minNumber) {
          minNumber = n;
        }
      }
      const numberKey =
        minNumber === Number.POSITIVE_INFINITY
          ? Number.MAX_SAFE_INTEGER
          : minNumber;
      const label = item.communication ?? "";
      return { numberKey, label };
    };

    const items = activeCommunication?.externalCommunicationItems ?? [];
    return items
      .filter((c) => (c.responseInfo?.associatedObjectIds?.length || 0) > 0)
      .sort((a, b) => {
        const keyA = sortKeyForPrimaryTask(a);
        const keyB = sortKeyForPrimaryTask(b);
        if (keyA.numberKey !== keyB.numberKey) {
          return keyA.numberKey - keyB.numberKey;
        }
        return keyA.label.localeCompare(keyB.label);
      });
  }, [
    activeCommunication?.externalCommunicationItems,
    documentQuery.data?.interrogatories,
  ]);

  const unassignedPrimaryTasks = useMemo(() => {
    return (
      activeCommunication?.externalCommunicationItems?.filter((c) => {
        return (
          !c?.responseInfo?.associatedObjectIds?.length &&
          !c.translatedExternalCommunicationItemId
        );
      }) ?? []
    );
  }, [activeCommunication?.externalCommunicationItems]);

  return {
    // route + auth + API clients
    documentId,
    user,
    documentApi,
    caseApi,
    clientApi,
    communicationApi,

    // document / case / client / communications queries
    documentQuery,
    caseQuery,
    clientQuery,
    communicationsQuery,
    reviewCommunications,
    activeCommunication,

    // loading and review / next-step gating
    isPageInitialDataLoading,
    isReviewLocked,
    isNextStepBlocked,

    // task presence, empty states, and request label
    primaryTaskCount,
    hasNoTasks,
    hasClientTasks,
    isEmptyClientTasksState,
    isSimplifyAllRemainingButtonVisible,
    requestName,

    // task lists for the main + unassigned sections
    primaryTasksSorted,
    unassignedPrimaryTasks,
  };
}

export type ReviewClientTasksPageData = ReturnType<
  typeof useReviewClientTasksPageData
>;
